import { FC, Suspense, useState } from 'react'
import { Outlet } from 'react-router-dom'
import {
  EnvironmentBanner,
  GreyGrey0,
  LayoutChromeProvider,
  PANEL_GUTTER,
  Panel,
  Toaster,
  Box,
  Flex,
  Grid,
  GridItem,
  Link
} from '@sequencehq/core-components'
import { NavigationV3 } from 'components/NavigationV3/NavigationV3'
import { MobileNavToggle } from 'components/NavigationV3/components/MobileNavToggle/MobileNavToggle'
import { getSentryConfig } from 'features/sentry/getSentryConfig'
import { NavigationV3Provider } from 'components/NavigationV3/NavigationV3Provider'
import { usePrefetchUsageEventTypesAndProperties } from 'lib/prefetching/usePrefetchUsageEventTypesAndProperties'
import { useAllUsers } from 'common/hooks/useUsers'
import { CollectEmailModal } from './modals/CollectEmailModal'
import { useLogMemoryUsage } from 'lib/postHog/useLogMemoryUsage'
import { useLogQueryCacheSize } from 'lib/postHog/useLogQueryCacheSize'
import { useMemoryThresholdReload } from 'lib/postHog/useMemoryThresholdReload'
import { isDemoEnv } from 'lib/environment/environment'
import { GlobalContractDropzone } from 'Automations/index'
import {
  PerformanceMonitorBar,
  useDevToolsEnabled
} from './PerformanceMonitorBar'
import { StandardLayoutModalPortalRoot } from './StandardLayoutModalPortalRoot'
import { GlobalSearchPalette } from 'modules/GlobalSearch'
import { useAtomValue } from 'jotai'
import { screenshotModeAtom } from './screenshotMode.atoms'

const { environment } = getSentryConfig()

const enableEmailCollectionModal =
  import.meta.env.VITE_DEMO_DISABLE_EMAIL_COLLECTION !== 'true'

const Layout: FC = () => {
  /**
   * For super common, but expensive, data retrievals we can prefetch them here.
   * These should also be persisted to indexedDB by configuring the query to be
   * persisted in the API package, where possible. But the preload helps with keeping
   * them up to date, and also helps with first load performance when the indexedDB
   * cache has been cleared away.
   */
  usePrefetchUsageEventTypesAndProperties()
  // Warm the all-users cache so approver pickers, execution summaries and other
  // user lists render without a fetch once opened.
  useAllUsers()

  /** Global logging (to Posthog) */
  useLogMemoryUsage()
  useLogQueryCacheSize()
  useMemoryThresholdReload()

  const screenshotMode = useAtomValue(screenshotModeAtom)
  const [devToolsEnabled] = useDevToolsEnabled()

  const [showEmailCollectionModal, setShowEmailCollectionModal] = useState(
    isDemoEnv() &&
      window.localStorage.getItem('collectEmail') !== 'false' &&
      enableEmailCollectionModal
  )

  return (
    <>
      <Toaster />
      <GlobalSearchPalette />
      {showEmailCollectionModal && (
        <CollectEmailModal onClose={() => setShowEmailCollectionModal(false)} />
      )}
      {!screenshotMode && (
        <Box
          data-testid="layout-environment-banner"
          width="100%"
          position="fixed"
          zIndex={3}
          pointerEvents="none"
        >
          {environment === 'demo' ? (
            <EnvironmentBanner environment={environment}>
              Demo -{' '}
              <Link
                to="https://access.sequencehq.com"
                isExternal
                pointerEvents="all"
              >
                Request a Sandbox
              </Link>
            </EnvironmentBanner>
          ) : (
            <EnvironmentBanner environment={environment} />
          )}
        </Box>
      )}
      <NavigationV3Provider>
        {/* Every page header gets the burger below `md`, so the drawer is
            reachable from any screen without each page opting in. */}
        <LayoutChromeProvider headerLeading={<MobileNavToggle />}>
          <Flex height="100dvh" width="100vw" bg={GreyGrey0}>
            {!screenshotMode && devToolsEnabled && <PerformanceMonitorBar />}
            <StandardLayoutModalPortalRoot>
              <Grid
                // On mobile the sidebar is a fixed slide-over drawer (out of
                // flow), so the navbar column collapses to 0 and the content
                // takes the full width.
                gridTemplateColumns={{ base: '0 1fr', md: 'auto 1fr' }}
                gridTemplateAreas={'"navbar content"'}
                height="100%"
                width="100%"
              >
                <GridItem data-testid="layout-sidebar" gridArea="navbar">
                  <NavigationV3 />
                </GridItem>
                {/**
                 * The content region is the gray backdrop; routed pages render
                 * their own floating white panel inside it. On desktop the gutter
                 * insets the panel from the viewport edge on the top/right/bottom
                 * (the panel sits flush against the nav — no left gutter). On
                 * mobile the gutter is dropped so the panel goes full-bleed
                 * edge-to-edge (Panel also squares off its corners below `md`).
                 */}
                <GridItem
                  gridArea="content"
                  boxSizing="border-box"
                  backgroundColor={GreyGrey0}
                  paddingTop={{ base: '0', md: PANEL_GUTTER }}
                  paddingRight={{ base: '0', md: PANEL_GUTTER }}
                  paddingBottom={{ base: '0', md: PANEL_GUTTER }}
                  paddingLeft="0"
                  zIndex="0"
                  width="100%"
                  height="100%"
                  overflow="auto"
                >
                  {/**
                   * A blank white panel as the fallback (rather than null) keeps
                   * the floating surface visible while a lazily-loaded route
                   * chunk resolves — otherwise the gray backdrop flashes through
                   * on overview -> detail navigation.
                   */}
                  <Suspense fallback={<Panel width="100%" height="100%" />}>
                    <GlobalContractDropzone>
                      <Outlet />
                    </GlobalContractDropzone>
                  </Suspense>
                </GridItem>
              </Grid>
            </StandardLayoutModalPortalRoot>
          </Flex>
        </LayoutChromeProvider>
      </NavigationV3Provider>
    </>
  )
}

export default Layout
